(() => {
  if (window.GBCategoryRowLoop?.booted) {
    window.GBCategoryRowLoop.init(document);
    return;
  }

  const SECTION_SELECTOR = "[data-gb-category-row]";
  const LIST_SELECTOR = "[data-gb-category-row-list]";
  const DESKTOP_QUERY = window.matchMedia("(min-width: 990px)");
  const REDUCED_MOTION_QUERY = window.matchMedia("(prefers-reduced-motion: reduce)");
  const instances = new Map();

  const listenMediaQuery = (mediaQueryList, callback) => {
    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", callback);
      return () => mediaQueryList.removeEventListener("change", callback);
    }

    mediaQueryList.addListener(callback);
    return () => mediaQueryList.removeListener(callback);
  };

  class CategoryRowLoop {
    constructor(section) {
      this.section = section;
      this.inner = section.querySelector(".gb-category-row__inner");
      this.list = section.querySelector(LIST_SELECTOR);
      const parsedSpeed = parseFloat(section.dataset.desktopLoopSpeed || "24");
      this.speed = Number.isFinite(parsedSpeed) && parsedSpeed > 0 ? parsedSpeed : 24;
      this.offset = 0;
      this.lastFrameTime = 0;
      this.rafId = 0;
      this.running = false;
      this.paused = false;
      this.initialItems = [];
      this.cleanupDesktopListener = null;
      this.cleanupReducedMotionListener = null;
    }

    init() {
      if (!this.inner || !this.list) return;
      this.initialItems = Array.from(this.list.children);

      this.onFrame = this.onFrame.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);

      this.section.addEventListener("pointerenter", this.onPointerEnter);
      this.section.addEventListener("pointerleave", this.onPointerLeave);
      this.section.addEventListener("focusin", this.onFocusIn);
      this.section.addEventListener("focusout", this.onFocusOut);
      window.addEventListener("resize", this.onResize, { passive: true });
      document.addEventListener("visibilitychange", this.onVisibilityChange);

      this.cleanupDesktopListener = listenMediaQuery(DESKTOP_QUERY, this.onMediaChange);
      this.cleanupReducedMotionListener = listenMediaQuery(REDUCED_MOTION_QUERY, this.onMediaChange);

      this.onMediaChange();
    }

    onMediaChange() {
      if (this.canRun()) {
        this.start();
        return;
      }

      this.stop(true);
    }

    onResize() {
      this.onMediaChange();
    }

    onVisibilityChange() {
      if (!this.running) return;
      this.lastFrameTime = 0;
    }

    onPointerEnter() {
      this.paused = true;
    }

    onPointerLeave() {
      this.paused = false;
      this.lastFrameTime = 0;
    }

    onFocusIn() {
      this.paused = true;
    }

    onFocusOut() {
      if (!this.section.contains(document.activeElement)) {
        this.paused = false;
        this.lastFrameTime = 0;
      }
    }

    canRun() {
      if (!DESKTOP_QUERY.matches || REDUCED_MOTION_QUERY.matches) return false;
      if (this.list.children.length < 2) return false;

      const listWidth = this.list.scrollWidth;
      const viewportWidth = this.inner.clientWidth;
      return listWidth > viewportWidth + 1;
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.section.dataset.desktopLoopRunning = "true";
      this.rafId = window.requestAnimationFrame(this.onFrame);
    }

    stop(resetTransform = false) {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }

      this.running = false;
      this.lastFrameTime = 0;
      this.section.dataset.desktopLoopRunning = "false";

      if (resetTransform) {
        this.offset = 0;
        this.restoreOriginalOrder();
        this.list.style.transform = "translate3d(0, 0, 0)";
      }
    }

    onFrame(timestamp) {
      if (!this.running) return;

      if (this.paused || document.hidden) {
        this.lastFrameTime = timestamp;
        this.rafId = window.requestAnimationFrame(this.onFrame);
        return;
      }

      if (!this.lastFrameTime) {
        this.lastFrameTime = timestamp;
      }

      const deltaMs = timestamp - this.lastFrameTime;
      this.lastFrameTime = timestamp;
      this.offset += (this.speed * deltaMs) / 1000;

      const gap = this.getGap();
      let firstItem = this.list.firstElementChild;

      while (firstItem) {
        const firstItemWidth = firstItem.getBoundingClientRect().width + gap;
        if (this.offset < firstItemWidth) break;

        this.offset -= firstItemWidth;
        this.list.appendChild(firstItem);
        firstItem = this.list.firstElementChild;
      }

      this.list.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
      this.rafId = window.requestAnimationFrame(this.onFrame);
    }

    getGap() {
      const style = window.getComputedStyle(this.list);
      const computedGap = parseFloat(style.columnGap || style.gap || "0");
      return Number.isFinite(computedGap) ? computedGap : 0;
    }

    restoreOriginalOrder() {
      if (!this.initialItems.length) return;

      this.initialItems.forEach((item) => {
        if (item.parentElement === this.list) {
          this.list.appendChild(item);
        }
      });
    }

    destroy() {
      this.stop(true);

      this.section.removeEventListener("pointerenter", this.onPointerEnter);
      this.section.removeEventListener("pointerleave", this.onPointerLeave);
      this.section.removeEventListener("focusin", this.onFocusIn);
      this.section.removeEventListener("focusout", this.onFocusOut);
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);

      if (typeof this.cleanupDesktopListener === "function") {
        this.cleanupDesktopListener();
      }

      if (typeof this.cleanupReducedMotionListener === "function") {
        this.cleanupReducedMotionListener();
      }
    }
  }

  const init = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (instances.has(section)) return;
      const instance = new CategoryRowLoop(section);
      instance.init();
      instances.set(section, instance);
    });
  };

  const destroyWithin = (root) => {
    instances.forEach((instance, section) => {
      if (root === section || root.contains(section)) {
        instance.destroy();
        instances.delete(section);
      }
    });
  };

  window.GBCategoryRowLoop = {
    booted: true,
    init,
    destroyWithin,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyWithin(event.target);
  });
})();
